import React from 'react';

import { useTagStyle } from '@/shared/hooks/useTagCatalog';

export interface TagChipProps {
  /** Nome da tag — é ele que resolve a cor no catálogo. */
  name: string;
  /**
   * `tinted` (padrão): fundo translúcido da própria cor. Usar onde o chip
   * senta num fundo estável.
   *
   * `solid`: fundo opaco do cartão, cor só na borda e no texto. Necessário
   * onde o chip pode sentar sobre um fundo de SELEÇÃO — no card de lead do
   * kanban, um fundo translúcido deixa passar o `--theme-surface-selected` e
   * o chip muda de cor ao selecionar o card.
   */
  variant?: 'tinted' | 'solid';
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  'data-testid'?: string;
}

/**
 * Chip de tag com a cor do catálogo.
 *
 * Existe para a cor ser resolvida em UM lugar. Antes de 22/09/2026 cada tela
 * inventava a sua: o drawer fazia hash sobre dez classes Tailwind, a lista de
 * conversas fazia hash sobre uma paleta DIFERENTE de dez cores, e o card de
 * lead, o painel de detalhes, os tickets e os clientes pintavam tudo de roxo.
 * A mesma tag tinha três aparências dependendo de onde você olhasse.
 *
 * Tag fora do catálogo (órfã) não fica cinza: cai no mesmo hash determinístico
 * que o catálogo usa para atribuir cor, então sai exatamente com a cor que
 * teria se estivesse cadastrada.
 */
export const TagChip: React.FC<TagChipProps> = ({
  name,
  variant = 'tinted',
  className,
  style,
  title,
  'data-testid': testId,
}) => {
  const tagStyle = useTagStyle();
  const { color, backgroundColor, borderColor } = tagStyle(name);

  return (
    <span
      className={className}
      title={title ?? name}
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: 9999,
        border: `1px solid ${borderColor}`,
        backgroundColor: variant === 'solid' ? 'var(--theme-card-background)' : backgroundColor,
        color,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {name}
    </span>
  );
};

export default TagChip;
